import type {UseQueryOptions, UseQueryResult} from '@tanstack/react-query'
import {useSafeEffectExtraDeps} from '@freckle/react-hooks'
import {staticExtra} from '@freckle/educator-entities/ts/common/helpers/extra-deps'
import {useAsync} from '@freckle/educator-entities/ts/common/hooks/use-async'
import type {StudentNameSortFieldT} from '@freckle/educator-entities/ts/users/models/teacher'
import {StudentNameSortField} from '@freckle/educator-entities/ts/users/models/teacher'
import type {LanguageSetting} from '@freckle/classroom/ts/store/settings'
import {useSettingsStore} from '@freckle/classroom/ts/store/settings'
import type {TeacherAttrs} from '@freckle/classroom/ts/users/models/teacher'
import {fetchCurrentTeacher} from '@freckle/classroom/ts/users/models/teacher.api'
import {useLanguage} from './use-language'

export const QUERY_KEY = 'fetchCurrentTeacher'

export function useTeacher(
  options?: Partial<UseQueryOptions<TeacherAttrs>>,
): UseQueryResult<TeacherAttrs> {
  const defaultOptions = {staleTime: 60 * 1000}
  const currentTeacher = useAsync(fetchCurrentTeacher, QUERY_KEY, [], {
    ...defaultOptions,
    ...options,
  })
  const {setLanguage} = useSettingsStore()
  const currentLang = useLanguage()

  const teacherLanguage = currentTeacher.data?.language

  useSafeEffectExtraDeps<{setLanguage: (languageSetting: LanguageSetting) => void}>(
    ({setLanguage}) => {
      if (teacherLanguage !== undefined && teacherLanguage !== currentLang)
        setLanguage({code: teacherLanguage, fromUrl: false})
    },
    [teacherLanguage, currentLang],
    {setLanguage: staticExtra(setLanguage)},
  )

  return currentTeacher
}

export const useStudentNameSortField = (): StudentNameSortFieldT =>
  // Since this comes from `useTeacher` (called by almost everything) the
  // reality is that this should never fallback to the default
  //
  // At the time of writing, >99% of teachers have "last_name" set in the DB. So
  // This is definitely a sane default in the unlikely case we haven't loaded the
  // teacher
  useTeacher().data?.studentNameSortField ?? StudentNameSortField.LastName
